import { useCallback, useEffect, useRef, useState } from "react";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { servicePost } from "../../api/client.ts";
import styles from "../../styles/formPage.module.scss";

/* ── Types ──────────────────────────────────────────────── */

interface ProductLock {
  Day: number;   // 1=Mon … 7=Sun
  From: string;  // e.g. "07:00"
  To: string;    // e.g. "22:00"
}

interface DayRow {
  enabled: boolean;
  from: string;
  to: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  productId: number;
}

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DEFAULT_FROM = "07:00";
const DEFAULT_TO = "22:00";
const KIOSK_ID = 1; // Hardcoded, matching WebAdmin2

function emptyDays(): DayRow[] {
  return DAY_NAMES.map(() => ({ enabled: false, from: DEFAULT_FROM, to: DEFAULT_TO }));
}

/* ── Component ──────────────────────────────────────────── */

export function ProductLockModal({ open, onClose, productId }: Props) {
  const [days, setDays] = useState<DayRow[]>(emptyDays());
  // Start in loading state so the first paint is the loading stub, not the
  // unpopulated default-days table — that prevented the layout flash where
  // users briefly saw empty rows before the spinner before the real data.
  const [loading, setLoading] = useState(() => open && productId > 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyLocks = useCallback((locks: ProductLock[], enableRows: boolean) => {
    setDays(prev => {
      const next: DayRow[] = prev.map(d => ({ ...d }));
      for (const lock of locks) {
        const idx = lock.Day - 1;
        if (idx >= 0 && idx < 7) {
          const row = next[idx]!;
          row.from = lock.From ?? DEFAULT_FROM;
          row.to = lock.To ?? DEFAULT_TO;
          if (enableRows) row.enabled = true;
        }
      }
      return next;
    });
  }, []);

  // Load lock hours when opened. Dedupe by productId so StrictMode's dev
  // double-mount issues exactly one WebGetProductLock (+ optional
  // WebGetDefaultProductLock fallback). Entry cleared on settle so a
  // reopen for the same product still refetches.
  const inFlightRef = useRef<{ id: number; promise: Promise<void> } | null>(null);
  useEffect(() => {
    if (!open || productId <= 0) return;
    setLoading(true);
    setError(null);

    let cancelled = false;
    const run = async (): Promise<void> => {
      const result = await servicePost<ProductLock[]>("WebGetProductLock", { productId, kioskId: KIOSK_ID });
      if (cancelled) return;
      if (result.bError) {
        setError(result.sMessage ?? "Failed to load lock hours");
        return;
      }
      const locks = result.oData ?? [];
      if (locks.length > 0) {
        applyLocks(locks, true);
        return;
      }
      const defResult = await servicePost<ProductLock[]>("WebGetDefaultProductLock", { productId });
      if (cancelled) return;
      if (!defResult.bError && defResult.oData?.length) {
        applyLocks(defResult.oData, false);
      } else {
        setDays(emptyDays());
      }
    };
    const promise = (
      inFlightRef.current?.id === productId
        ? inFlightRef.current.promise
        : (inFlightRef.current = { id: productId, promise: run() }).promise
    ).finally(() => {
      if (inFlightRef.current?.id === productId) inFlightRef.current = null;
      if (!cancelled) setLoading(false);
    });
    void promise;
    return () => { cancelled = true; };
  }, [open, productId, applyLocks]);

  const handleToggle = useCallback((idx: number) => {
    setDays(prev => {
      const next = [...prev];
      const row = next[idx]!;
      next[idx] = { ...row, enabled: !row.enabled };
      return next;
    });
  }, []);

  const handleTimeChange = useCallback((idx: number, field: "from" | "to", value: string) => {
    setDays(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [field]: value };
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    const pl: ProductLock[] = [];
    for (let i = 0; i < 7; i++) {
      const d = days[i]!;
      if (d.enabled) {
        pl.push({ Day: i + 1, From: d.from + ":00", To: d.to + ":00" });
      }
    }
    try {
      const result = await servicePost<boolean>("WebSaveProductLock", {
        productId,
        kioskId: KIOSK_ID,
        pl,
      });
      if (result.bError || !result.oData) {
        setError("Error! Make sure the times entered are properly formatted.");
      } else {
        onClose();
      }
    } catch {
      setError("Failed to save lock hours.");
    } finally {
      setSaving(false);
    }
  }, [days, productId, onClose]);

  return (
    <GridEditModal
      open={open}
      title={`Available Hours (P: ${productId}, K: ${KIOSK_ID})`}
      saving={saving || loading}
      savingLabel={loading ? "Loading…" : undefined}
      statusMessage={error ? { type: "error", message: error } : null}
      onClose={onClose}
      onSave={handleSave}
    >
      {/* Keep the day grid mounted at all times — the loading state only
          disables interaction; the layout stays identical so opening the
          modal doesn't flash between "Loading…" and the real rows. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? "none" : undefined,
          transition: "opacity var(--duration-fast) var(--ease-default)",
        }}
        aria-busy={loading || undefined}
      >
        {/* Header row */}
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", paddingBottom: "var(--space-1)", borderBottom: "1px solid var(--color-border-default)" }}>
          <div style={{ width: 28 }} />
          <div style={{ width: 100, fontWeight: "var(--font-semibold)" as never, fontSize: "var(--text-sm)" }}>Day</div>
          <div style={{ flex: 1, fontWeight: "var(--font-semibold)" as never, fontSize: "var(--text-sm)" }}>From</div>
          <div style={{ flex: 1, fontWeight: "var(--font-semibold)" as never, fontSize: "var(--text-sm)" }}>To</div>
        </div>
        {days.map((day, idx) => (
          <div key={idx} style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={day.enabled}
              onChange={() => handleToggle(idx)}
              disabled={loading}
              style={{ width: 20, height: 20, cursor: loading ? "default" : "pointer" }}
            />
            <div style={{ width: 100, fontSize: "var(--text-sm)", color: day.enabled ? "var(--color-text)" : "var(--color-text-muted)" }}>
              {DAY_NAMES[idx]}
            </div>
            <input
              type="time"
              className={styles.fieldInput}
              value={day.from}
              onChange={e => handleTimeChange(idx, "from", e.target.value)}
              disabled={loading || !day.enabled}
              style={{ flex: 1 }}
            />
            <input
              type="time"
              className={styles.fieldInput}
              value={day.to}
              onChange={e => handleTimeChange(idx, "to", e.target.value)}
              disabled={loading || !day.enabled}
              style={{ flex: 1 }}
            />
          </div>
        ))}
      </div>
    </GridEditModal>
  );
}
